import { defineStore } from "pinia";
import { JmApi } from "../jm-api/jm-api";
import { USER_QUOTA_URL } from "../config/urls";

/**
 * Shape of the UserQuota payload returned by GET /user-quota.
 */
export interface UserQuota {
  userId: string;
  dailyLimit: number;
  monthlyLimit: number;
  dailyUsed: number;
  monthlyUsed: number;
  dailyRemaining: number;
  monthlyRemaining: number;
}

export const useQuotaStore = defineStore("quota", {
  state: () => ({
    quota: undefined as UserQuota | undefined,
    loading: false as boolean,
    error: undefined as string | undefined,
  }),
  getters: {
    /** Smallest remaining budget across both periods (the effective limit). */
    effectiveRemaining(state): number | undefined {
      if (!state.quota) return undefined;
      return Math.min(state.quota.dailyRemaining, state.quota.monthlyRemaining);
    },
  },
  actions: {
    /** Fetches the current user's own import quota; the user is derived from the token. */
    async fetchUserQuota(): Promise<UserQuota | undefined> {
      this.loading = true;
      this.error = undefined;
      try {
        // JmApi.get returns the parsed JSON body (object); cast to our typed shape.
        const response = (await JmApi.get(USER_QUOTA_URL)) as unknown as UserQuota;
        this.quota = response;
        return this.quota;
      } catch (error) {
        this.error =
          error && typeof error === "object" && "message" in error
            ? (error as { message?: string }).message
            : "Kontingent konnte nicht geladen werden.";
        console.error("Error fetching user quota:", error);
        return undefined;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Estimates how many import queries a planned import would consume.
     * Upper bound only: paths x transports, without server-side deduplication.
     */
    estimatePlanned(numberOfPaths: number, transportCount: number): number {
      return Math.max(0, numberOfPaths * transportCount);
    },

    /** True when the planned (estimated) import fits within both remaining budgets. */
    fitsWithinQuota(numberOfPaths: number, transportCount: number): boolean {
      if (!this.quota) return true; // no quota info -> do not block client-side
      const planned = this.estimatePlanned(numberOfPaths, transportCount);
      return (
        planned <= this.quota.dailyRemaining &&
        planned <= this.quota.monthlyRemaining
      );
    },
  },
});
